/**
 * Tutor create form.
 * Builds the profile and criminal-record decision in one submit (no full-state hydrate).
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useCreateInPersonTutorProfile,
  useEligibleEstablishments,
  useUpdateCriminalRecordDecision,
} from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { usePageActions } from '@/hooks/usePageActions';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { getSubjects } from '@/services/subjects.service';
import { sortByInpersonSubjectLabel } from '@/utils/inperson-subject-order-utils';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { getCompanyCountry } from '@/stores/themeStore';
import {
  areContactsIdentical,
  isPrimaryContactFormValid,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { useFormValidation } from '@/hooks/useFormValidation';
import { tutorInPersonCreateValidationSchema } from '@/utils/validation-schemas-utils';
import { useContactExistsCheck, resolveContactCheckType } from '@/hooks/useContactExistsCheck';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  applyCriminalRecordOnCreate,
  applyInPersonTutorFieldUpdate,
  buildTutorProfileDto,
  checkEngagementEndDateError,
  computeInPersonAllowedSlotsFromPrograms,
  defaultUpload,
  filterOptionsBySearchQuery,
  mapEstablishmentOptions,
  mergePendingUploads,
  setNestedPath,
  validateInPersonProSituationFields,
} from './tutor-form-utils';
import { hasPendingUploadFiles, uploadPendingFiles } from '@/utils/pending-upload-utils';
import { isCriminalRecordMetadataComplete } from '@/utils/criminal-record-utils';
import { buildPendingDocumentFilePatch } from '@/utils/pending-document-link-utils';
import { TutorPersonalAccountSection } from './tutor-form-builder/TutorPersonalAccountSection';
import { TutorSupportSection } from './tutor-form-builder/TutorSupportSection';
import { TutorInformationMeetingSection } from './tutor-form-builder/TutorInformationMeetingSection';
import { TutorOfficialDocumentsSection } from './tutor-form-builder/TutorOfficialDocumentsSection';
import { TutorCertificationSection } from './tutor-form-builder/TutorCertificationSection';
import { TutorContractSection } from './tutor-form-builder/TutorContractSection';
import { TutorPermissionsSection } from './tutor-form-builder/TutorPermissionsSection';

/** Create-tutor slide-panel props. */
interface CreateTutorViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Tutor create form. */
export function CreateTutorView({ onClose, onSuccess }: Readonly<CreateTutorViewProps>) {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const defaultCountry = useDefaultPhoneCountry();
  const tutorPageActions = usePageActions('tutors');
  const validation = useFormValidation(tutorInPersonCreateValidationSchema);

  const { data: subjects = [] } = useQuery({
    queryKey: ['subjects', 'presentiel'],
    queryFn: async () =>
      (await getSubjects({ page: 1, pageSize: 20, displayOrder: 'presentiel' })).data,
  });

  const { data: programsResponse } = useProgramsForMode();
  const programsData = programsResponse?.data ?? [];

  /** Paginated subjects as {@link Select} options. */
  const subjectOptions = React.useMemo(
    () =>
      sortByInpersonSubjectLabel(
        (subjects as any[]).map((s: any) => ({
          value: s.id as string,
          label: s.label as string,
        })),
      ),
    [subjects],
  );

  const [formData, setFormData] = React.useState<Record<string, any>>({
    tutoringType: 'in_person',
    professionalSituation: 'student',
    country: getCompanyCountry(),
    hasParticipated: false,
    canAddStudentsToSession: false,
    canRemoveStudentsFromSession: false,
    preValidated: false,
    validated: false,
  });

  const [pendingFiles, setPendingFiles] = React.useState<Record<string, File | null>>({});
  const [isUploading, setIsUploading] = React.useState(false);

  const { exists: contactExists, isChecking: isPrimaryChecking } = useContactExistsCheck({
    value: formData.contact || '',
    type: resolveContactCheckType(formData.contactType ?? formData._contactType),
    enabled:
      (formData.contactType ?? formData._contactType) !== 'unknown' &&
      (formData.contact || '').trim().length > 0,
  });

  const secondaryContactType =
    (formData.contactType ?? formData._contactType) === 'email' ? 'phone' : 'email';
  const { exists: secondaryContactExists, isChecking: isSecondaryChecking } = useContactExistsCheck(
    {
      value: formData.secondaryContact || '',
      type: secondaryContactType,
      enabled:
        (formData.contactType ?? formData._contactType) !== 'unknown' &&
        (formData.secondaryContact || '').trim().length > 0,
    },
  );

  const contactsAreIdentical = areContactsIdentical(
    formData.contact || '',
    formData.secondaryContact || '',
  );

  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;

  React.useEffect(() => {
    validation.validateInitialFormData({
      ...formDataRef.current,
      _contactType: formDataRef.current.contactType,
    });
  }, []);

  const selectedDepartments = (formData.departments ?? []) as string[];
  const { data: eligibleEstablishments = [], isLoading: isLoadingEstablishments } =
    useEligibleEstablishments(selectedDepartments, companyId, {
      enabled: selectedDepartments.length > 0,
    });

  /** Eligible establishments for the selected departments ({@link Select} options). */
  const allEstablishmentOptions = React.useMemo(
    () => mapEstablishmentOptions(eligibleEstablishments as Array<{ id?: string; name?: string }>),
    [eligibleEstablishments],
  );

  const [establishmentSearch, setEstablishmentSearch] = React.useState('');
  /** Client-side filter of {@link allEstablishmentOptions} by the search box. */
  const establishmentOptions = React.useMemo(
    () => filterOptionsBySearchQuery(allEstablishmentOptions, establishmentSearch),
    [allEstablishmentOptions, establishmentSearch],
  );

  /** Availability slots allowed by the selected programs (`undefined` = no restriction). */
  const allowedSlots = React.useMemo(
    () => computeInPersonAllowedSlotsFromPrograms(programsData as any[], formData.programs),
    [formData.programs, programsData],
  );

  const createMutation = useCreateInPersonTutorProfile();
  const criminalRecordDecisionMutation = useUpdateCriminalRecordDecision();

  /** Set one top-level field. Changing departments clears selected establishments. */
  const set = (field: string, value: any) => {
    setFormData((prev) => {
      const next = applyInPersonTutorFieldUpdate(prev, field, value);
      validation.validateField(field, value, { ...next, _contactType: next.contactType });
      validation.markAsTouched(field);
      validation.markAsDirty(field);
      return next;
    });
  };

  /** Patch several fields at once (supports dotted paths). Validates after the state flush. */
  const setBatch = (patch: Record<string, any>) => {
    setFormData((prev) => {
      let next = { ...prev };
      Object.entries(patch).forEach(([key, value]) => {
        if (key.includes('.')) {
          next = setNestedPath(next, key, value);
        } else {
          next[key] = value;
        }
      });
      formDataRef.current = next;
      return next;
    });
    setTimeout(() => {
      Object.entries(patch).forEach(([k, v]) => {
        validation.validateField(k, v, formDataRef.current);
        validation.markAsTouched(k);
        validation.markAsDirty(k);
      });
    }, 0);
  };

  /** Blocking error when engagement end is before start. */
  const engagementEndDateError = React.useMemo(
    () =>
      checkEngagementEndDateError(
        formData.engagementStartDate as string | undefined,
        formData.engagementEndDate as string | undefined,
      ),
    [formData.engagementStartDate, formData.engagementEndDate],
  );

  /** Merge one key into `formData.criminalRecord`. */
  const setCriminalRecord = (key: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      criminalRecord: { ...prev.criminalRecord, [key]: value },
    }));
  };

  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  /** Student / high-school / other require establishment and/or valued engagement. */
  const proSituationError = React.useMemo(
    () => validateInPersonProSituationFields(formData),
    [
      formData.professionalSituation,
      formData.proSituationEstablishmentId,
      formData.proSituationAssociativeEngagement,
    ],
  );

  /** Store the file for submit upload and write a pending link so required docs validate. */
  const handleFileChange = (key: string, file: File | null) => {
    setPendingFiles((prev) => ({ ...prev, [key]: file }));
    const patch = buildPendingDocumentFilePatch(key, file);
    if (patch) setBatch(patch);
  };

  /** Validate, upload pending files, POST profile, then apply the criminal-record decision. */
  const handleSubmit = async () => {
    setSubmitAttempted(true);
    const isValid = validation.validateForm({
      ...formData,
      _contactType: formData.contactType,
      _contactExists: contactExists,
    });
    if (!isValid) {
      toast.error(getErrorMessage('requiredFields'));
      return;
    }
    if (proSituationError) {
      toast.error(getErrorMessage(proSituationError));
      return;
    }
    if (engagementEndDateError) return;

    if (hasPendingUploadFiles(pendingFiles)) {
      setIsUploading(true);
    }
    let uploadedUrls: Record<string, string> = {};
    try {
      uploadedUrls = await uploadPendingFiles(pendingFiles, defaultUpload);
    } catch (err) {
      toast.error(`Erreur lors de l'upload : ${getErrorMessage(err)}`);
      setIsUploading(false);
      return;
    }

    const enrichedData = mergePendingUploads(formData, uploadedUrls);

    const dto = buildTutorProfileDto('create', enrichedData, defaultCountry);

    const run = async () => {
      try {
        const result = await createMutation.mutateAsync({ dto, companyId });
        const userId = (result as any)?.user?.id as string | undefined;
        await applyCriminalRecordOnCreate(
          userId,
          formData.criminalRecord?.markValidated === true,
          companyId,
          criminalRecordDecisionMutation.mutateAsync,
        );
        toast.success('Tuteur créé avec succès');
        onSuccess?.();
      } catch (error: unknown) {
        toast.error(getErrorMessage(error));
      }
    };
    void run();
    setIsUploading(false);
  };

  /** Nested criminal-record slice (empty object until the user fills it). */
  const criminalRecord = formData.criminalRecord ?? {};
  /** Number, issue date, and authority are all present — unlock validate checkbox. */
  const allCriminalRecordFilled = isCriminalRecordMetadataComplete(criminalRecord);

  return (
    <CollapseReplaceForm
      formType="create"
      title="Créer un tuteur"
      subtitle="Renseignez les informations du tuteur"
      onClose={onClose}
      onSubmit={handleSubmit}
      isSubmitting={isUploading || createMutation.isPending}
      isValid={
        isPrimaryContactFormValid({
          primaryExists: contactExists,
          secondaryExists: secondaryContactExists,
          isPrimaryChecking,
          isSecondaryChecking,
          identical: contactsAreIdentical,
          formValid: validation.isFormValid(),
        }) &&
        !engagementEndDateError &&
        !proSituationError
      }
      submitLabel="Créer"
    >
      <div className="space-y-6">
        <TutorPersonalAccountSection
          mode="create"
          formData={formData}
          fullState={undefined}
          defaultCountry={defaultCountry}
          set={set}
          submitAttempted={submitAttempted}
          contactExists={contactExists}
          secondaryContactExists={secondaryContactExists}
          validation={validation}
        />
        <TutorSupportSection
          mode="create"
          formData={formData}
          fullState={undefined}
          programsData={programsData}
          subjectOptions={subjectOptions}
          establishmentOptions={establishmentOptions}
          selectedDepartments={selectedDepartments}
          isLoadingEstablishments={isLoadingEstablishments}
          allowedSlots={allowedSlots}
          tutorPageActions={tutorPageActions}
          set={set}
          setEstablishmentSearch={setEstablishmentSearch}
          engagementEndDateError={engagementEndDateError}
        />
        <TutorOfficialDocumentsSection
          mode="create"
          tutorUserId={undefined}
          criminalRecord={criminalRecord}
          allCriminalRecordFilled={allCriminalRecordFilled}
          set={set}
          setCriminalRecord={setCriminalRecord}
          setBatch={setBatch}
          onFileSelect={handleFileChange}
        />
        <TutorInformationMeetingSection
          mode="create"
          formData={formData}
          tutorUserId={undefined}
          companyId={companyId}
          user={user}
          set={set}
          getFieldError={validation.getFieldError}
        />
        <TutorCertificationSection formData={formData} set={set} onFileSelect={handleFileChange} />
        <TutorContractSection
          formData={formData}
          set={set}
          setBatch={setBatch}
          onFileSelect={handleFileChange}
        />
        <TutorPermissionsSection formData={formData} set={set} />
      </div>
    </CollapseReplaceForm>
  );
}
